import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { observable } from 'mobx';

import MessagePropType from '../definitions/MessagePropType';
import MessageStyles from '../definitions/MessageStyles';
import focusNextElementInForm from '../functions/focusNextElementInForm';
import useForm from '../hooks/useForm';
import PositionOffscreenStyles from '../styles/PositionOffscreenStyles';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import FieldPropType from '../definitions/FieldPropType';
import useFieldset from '../hooks/useFieldset';
import isFieldRequired from '../functions/isFieldRequired';
import { ALIGNMENT_OPTIONS } from './Tooltip';
import QuestionContainer from './QuestionContainer';
import InputSelector from './InputSelector';
import Fieldset from './Fieldset';
import Paragraph from './Paragraph';
import ErrorMessage from './ErrorMessage';
import { AnimationTypePropType, ANIMATION_TYPES } from './AnimatedContainer';


function QuestionMultiInput({
    fields,

    labelMessage,
    hideLabels = false,
    isInline = false,
    shouldWrap = false,

    tooltipMessage,
    tooltipAlignment,

    quicktipMessage,

    isVisible = true,
    withColour = true,
    animationType = ANIMATION_TYPES.FADE,

    className,

    onComplete,
    onChooseErrorMessage,

    errorMessageStyle = MessageStyles.DEFAULT,

    isSideBySide,
    shouldRemoveMargin,

    children,
    ...otherProps
}) {
    const [ fieldsetState, setFieldsetState ] = useState(undefined);
    const [ hasClickedComplete, setHasClickedComplete ] = useState(false);
    const validityState = useValidityState();

    const form = useForm();
    const fieldset = useFieldset();
    const isAutoFocusEnabled = fieldset ? fieldset.isAutoFocusEnabled : !!form?.isAutoFocusEnabled;

    const fieldsetId = fields[0].field.name + '-fieldset';
    const tooltipId = fields[0].field.name + '-tooltip';

    const shouldQuestionShowInvalid = errorMessageStyle !== MessageStyles.HIDDEN && validityState.shouldShowInvalid;

    const shouldEnableComplete = isAutoFocusEnabled && !shouldQuestionShowInvalid;

    const shouldShowErrorMessages = errorMessageStyle === MessageStyles.DEFAULT;
    const errorMessage = shouldQuestionShowInvalid && shouldShowErrorMessages
        ? onChooseErrorMessage
            ? onChooseErrorMessage(validityState.errorMessages)
            : validityState.firstErrorMessage
        : null;

    function handleKeyPress(e) {
        if (e.key === 'Enter') {
            handleComplete();
        }
    }

    async function handleComplete() {
        if (!isAutoFocusEnabled) {
            return;
        }

        setHasClickedComplete(true);

        if (!fieldsetState.isValid) {
            return;
        }

        onComplete?.();

        await focusNextElementInForm(fields[0].field.name);
    }

    return (
        <StyledQuestionContainer
            className={className}
            labelFor={fieldsetId}
            labelMessage={labelMessage}
            hideLabels={hideLabels}
            tooltipId={tooltipId}
            tooltipMessage={tooltipMessage}
            tooltipAlignment={tooltipAlignment}
            quicktipMessage={quicktipMessage}
            isVisible={isVisible}
            withColour={withColour}
            animationType={animationType}
            isInvalid={shouldShowErrorMessages && shouldQuestionShowInvalid}
            isOptional={areAllFieldsOptional(fields, form)}
            isSideBySide={isSideBySide}
            shouldRemoveMargin={shouldRemoveMargin}
            data-question-name={fields[0].field.name}
            onCompleteButtonClick={shouldEnableComplete ? handleComplete : undefined}
            showCompleteButtonEnterText
            onKeyPress={shouldEnableComplete ? handleKeyPress : undefined}
            // In Question, otherProps is spread onto the one and only InputSelector.
            // We can't do that with multiple InputSelectors so this component
            // spreads it here. Be aware of this difference when passing via otherProps!
            {...otherProps}
        >
            <Fieldset
                id={fieldsetId}
                onCreateState={context => {
                    setFieldsetState(context);
                }}
                className={classNames({
                    'inline-fieldset': isInline,
                    'should-wrap': shouldWrap,
                })}
            >
                <For
                    each="field"
                    of={fields}
                >
                    <SingleInputContainer
                        field={field}
                        tooltipId={tooltipId}
                        hideLabels={hideLabels}
                        isVisible={isVisible}
                        shouldShowErrorMessages={shouldShowErrorMessages}
                        hasClickedComplete={hasClickedComplete}
                        key={field.field.name}
                        onValidityChange={validityState.setValidityState}
                        canShowInvalid={hasClickedComplete}
                    />
                </For>
            </Fieldset>

            <If condition={errorMessage}>
                <ErrorMessage
                    className="group-error-message-pill"
                    message={errorMessage}
                />
            </If>

            {children}
        </StyledQuestionContainer>
    );
}

QuestionMultiInput.propTypes = {
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            field: FieldPropType.isRequired,
            label: MessagePropType.isRequired,
            subLabel: MessagePropType,
            type: PropTypes.string,
            placeholder: MessagePropType,
            flex: PropTypes.string,
        }),
    ).isRequired,

    labelMessage: MessagePropType,
    hideLabels: PropTypes.bool,

    tooltipMessage: MessagePropType,
    tooltipAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),

    quicktipMessage: MessagePropType,

    isVisible: PropTypes.bool,
    withColour: PropTypes.bool,
    animationType: AnimationTypePropType,

    className: PropTypes.string,

    onComplete: PropTypes.func,
    // Returns either a MessagePropType or an array of messages of MessagePropType
    onChooseErrorMessage: PropTypes.func,
    // allow to override display of error messages
    errorMessageStyle: PropTypes.oneOf(Object.values(MessageStyles)),
    shouldWrap: PropTypes.bool,

    children: PropTypes.node,
    isInline: PropTypes.bool,
    isSideBySide: PropTypes.bool,
    shouldRemoveMargin: PropTypes.bool,
};


// Disabling this rule since the label and InputSelector below are causing it to
//  fail even though we have a valid htmlFor attribute
/* eslint-disable jsx-a11y/label-has-associated-control */
function SingleInputContainer({
    field,
    tooltipId,
    hideLabels = false,
    isVisible = true,
    canShowInvalid = false,
    onValidityChange,
}) {
    // Memoized because inputs expect a stable reference; if we don't memoize, it'll re-raise validity every render.
    const handleValidityChange = useCallback(validityState => {
        onValidityChange({ name: field.field.name, ...validityState });
    }, [ field.field.name, onValidityChange ]);

    return (
        <StyledSpan
            className="question-container-span"
            key={field.field.name}
            flex={field?.flex}
        >
            <label
                className={classNames('rh-title-xs', 'rh-display-block', 'rh-my-0', 'field-labels')}
                htmlFor={field.field.name}
            >
                <Paragraph
                    forwardedAs="span"
                    className="main-label"
                    message={field.label}
                    size="medium"
                    weight="medium"
                />

                <If condition={field?.subLabel}>
                    <Paragraph
                        forwardedAs="span"
                        className="sub-label"
                        message={field.subLabel}
                        size="small"
                    />
                </If>
            </label>
            <InputSelector
                className="field-input"
                field={field.field}
                type={field.type}
                aria-describedby={tooltipId}
                placeholder={hideLabels ? field.label : field.placeholder}
                isVisible={isVisible}
                errorMessageStyle={MessageStyles.MINIMAL}
                {...getInputProps(field)}
                canShowInvalid={canShowInvalid}
                onValidityChange={handleValidityChange}
            />
        </StyledSpan>
    );
}

SingleInputContainer.propTypes = {
    field: PropTypes.shape({
        field: FieldPropType.isRequired,
        label: MessagePropType.isRequired,
        subLabel: MessagePropType,
        type: PropTypes.string,
        placeholder: MessagePropType,
        flex: PropTypes.string,
    }).isRequired,

    tooltipId: PropTypes.string.isRequired,
    hideLabels: PropTypes.bool,

    isVisible: PropTypes.bool,

    canShowInvalid: PropTypes.bool,
    onValidityChange: PropTypes.func.isRequired,
};


function areAllFieldsOptional(fields, form) {
    return fields.every(field => isFieldRequired(field.field, form) === false);
}

// Have to isolate field-specific props that will get spread onto InputSelector
function getInputProps(fieldProps) {
    // eslint-disable-next-line no-unused-vars
    const { field, label, subLabel, type, errorMessageStyle, placeholder, ...inputProps } = fieldProps;

    return inputProps;
}

function useValidityState() {
    const [ state ] = useState(() => observable({
        validityStates: new Map(),

        setValidityState({ name, ...validityState }) {
            state.validityStates.set(name, validityState);
        },

        getValidityState(name) {
            return state.validityStates.get(name);
        },

        get shouldShowInvalid() {
            return Array.from(state.validityStates.values()).some(validityState => validityState.isShowingInvalid);
        },

        get errorMessages() {
            return Array
                .from(state.validityStates.entries())
                .filter(([ , validityState ]) => validityState.isShowingInvalid)
                .reduce((errors, [ name, validityState ]) => ({
                    ...errors,
                    [name]: validityState.errorMessage,
                }), {});
        },

        get firstErrorMessage() {
            return Array.from(state.validityStates.values()).find(validityState => validityState.isShowingInvalid)?.errorMessage;
        },
    }));

    return state;
}

const StyledQuestionContainer = styled(QuestionContainer)`
    fieldset {
        /* Counter the margin of the fields */
        margin: -${Sizes.SPACING.HALF} 0;
    }

    .inline-fieldset {
        display: flex;
        flex-direction: row;
        gap: ${Sizes.SPACING.HALF};

        &.should-wrap {
            flex-wrap: wrap;
            flex-basis: 200px;
        }
    }

    .question-container-span {
        display: flex;
        flex-grow: 1;
        align-items: center;
        justify-content: space-between;

        margin: ${Sizes.SPACING.HALF} 0;

        > .field-labels {
            > .main-label,
            > .sub-label {
                display: block;
                margin-top: 0;
                margin-bottom: 0;
            }
            > .sub-label {
                line-height: 1.35;
                color: ${Colours.STONE_DARKEST};
            }
        }

        /* Inputs are typically in an ErrorMessageContainer, but sometimes not, I believe.
        Just give 'em all the same flex-basis. */
        > .field-labels,
        > .error-message-container,
        > .field-input {
            flex-basis: 48%;
        }

        ${props => props.hideLabels && `
            > .field-labels {
                ${PositionOffscreenStyles};
            }

            > .error-message-container,
            > .field-input {
                flex-grow: 1;
            }
        `}
    }

    .group-error-message-pill {
        display: block;
        width: fit-content;
        margin: ${Sizes.SPACING.HALF} 0 ${Sizes.SPACING.HALF} auto;
    }
`;

const StyledSpan = styled.span`
    flex: ${props => props.flex};
`;

export default observer(QuestionMultiInput);
